import {useCallback,useEffect,useLayoutEffect,useRef,useState} from 'react'
import {createPortal} from 'react-dom'
import {ChevronLeft,ChevronRight,RotateCcw,X,ZoomIn,ZoomOut} from 'lucide-react'
import {readFoscPublicationFile} from './readFoscPublicationFile'
import {loadPdfJs,pdfResourceOptions} from './createPdfJsLoader'
import './FoscPdfViewer.css'

const COPY={
  fr:{title:'Publication officielle FOSC',close:'Fermer le document',in:'Agrandir',out:'Réduire',reset:'Ajuster à la largeur',prev:'Page précédente',next:'Page suivante',page:'Page',of:'sur',loading:'Chargement du document…',error:'Impossible de charger cette publication. Réessayez.',session:'Votre session a changé. Fermez le document et reconnectez-vous.',large:'Ce document dépasse les limites de lecture.',retry:'Réessayer',hint:'Faites glisser le document et pincez avec deux doigts pour zoomer.',document:'Document FOSC'},
  de:{title:'Amtliche SHAB-Publikation',close:'Dokument schliessen',in:'Vergrössern',out:'Verkleinern',reset:'An die Breite anpassen',prev:'Vorherige Seite',next:'Nächste Seite',page:'Seite',of:'von',loading:'Dokument wird geladen…',error:'Die Publikation konnte nicht geladen werden. Versuchen Sie es erneut.',session:'Ihre Sitzung hat sich geändert. Schliessen Sie das Dokument und melden Sie sich erneut an.',large:'Dieses Dokument überschreitet die Anzeigegrenzen.',retry:'Erneut versuchen',hint:'Ziehen Sie das Dokument und zoomen Sie mit zwei Fingern.',document:'SHAB-Dokument'},
  it:{title:'Pubblicazione ufficiale FUSC',close:'Chiudi il documento',in:'Ingrandisci',out:'Riduci',reset:'Adatta alla larghezza',prev:'Pagina precedente',next:'Pagina successiva',page:'Pagina',of:'di',loading:'Caricamento del documento…',error:'Impossibile caricare la pubblicazione. Riprovi.',session:'La sessione è cambiata. Chiuda il documento e acceda nuovamente.',large:'Il documento supera i limiti del lettore.',retry:'Riprova',hint:'Trascini il documento e usi due dita per ingrandire.',document:'Documento FUSC'},
  en:{title:'Official SOGC publication',close:'Close document',in:'Zoom in',out:'Zoom out',reset:'Fit to width',prev:'Previous page',next:'Next page',page:'Page',of:'of',loading:'Loading document…',error:'This publication could not be loaded. Please try again.',session:'Your session has changed. Close the document and sign in again.',large:'This document exceeds the reader limits.',retry:'Try again',hint:'Drag the document and pinch with two fingers to zoom.',document:'SOGC document'},
}
export const clampPdfZoom=value=>Math.max(0.5,Math.min(4,Number.isFinite(value)?value:1))
export function pdfCanvasSize(page,width,zoom=1,dpr=1) {
  if(!page||!Number.isFinite(page.width)||!Number.isFinite(page.height)||page.width<=0||page.height<=0||page.height/page.width>20||page.width/page.height>20)throw Object.assign(new Error('large'),{code:'large'})
  const fit=Math.max(1,width-32)/page.width,scale=fit*clampPdfZoom(zoom)
  const cssWidth=page.width*scale,cssHeight=page.height*scale
  const ratio=Math.min(Math.max(1,Number.isFinite(dpr)?dpr:1),2,Math.sqrt(12000000/(cssWidth*cssHeight)),16384/cssWidth,16384/cssHeight)
  return{scale,cssWidth,cssHeight,ratio,width:Math.max(1,Math.floor(cssWidth*ratio)),height:Math.max(1,Math.floor(cssHeight*ratio))}
}

export default function FoscPdfViewer({url,title,lang='fr',userId,onClose,restoreFocusTo}) {
  const text=COPY[['fr','de','it','en'].includes(lang)?lang:'fr']
  const dialog=useRef(null),viewport=useRef(null),canvas=useRef(null),close=useRef(onClose),returnFocus=useRef(restoreFocusTo)
  close.current=onClose
  const [attempt,setAttempt]=useState(0),[resource,setResource]=useState(null),[status,setStatus]=useState('loading'),[error,setError]=useState('')
  const [pageNumber,setPageNumber]=useState(1),[zoom,setZoom]=useState(1),[renderZoom,setRenderZoom]=useState(1),[layoutWidth,setLayoutWidth]=useState(0),[renderBusy,setRenderBusy]=useState(false),[renderError,setRenderError]=useState('')
  const scope=String(userId)+':'+String(url)+':'+attempt
  const activeScope=useRef(scope),zoomRef=useRef(zoom),anchor=useRef(null),pointers=useRef(new Map()),pinch=useRef(null)
  activeScope.current=scope;zoomRef.current=zoom
  const pdf=resource?.scope===scope?resource.pdf:null
  const destroyDocument=resource?.destroy
  useLayoutEffect(()=>{
    const previous=returnFocus.current||document.activeElement,body=document.body
    const overflow=body.style.getPropertyValue('overflow'),priority=body.style.getPropertyPriority('overflow')
    body.style.setProperty('overflow','hidden')
    dialog.current?.querySelector('[data-pdf-close]')?.focus()
    const trap=event=>{
      if(event.key==='Escape'){event.preventDefault();event.stopPropagation();close.current?.();return}
      if(event.key!=='Tab')return
      const buttons=[...dialog.current.querySelectorAll('button:not([disabled]),[tabindex="0"]')].filter(node=>!node.hidden)
      const first=buttons[0],last=buttons[buttons.length-1],focused=document.activeElement
      if(event.shiftKey&&(focused===first||!dialog.current.contains(focused))){event.preventDefault();last?.focus()}
      else if(!event.shiftKey&&(focused===last||!dialog.current.contains(focused))){event.preventDefault();first?.focus()}
    }
    document.addEventListener('keydown',trap,true)
    return()=>{
      document.removeEventListener('keydown',trap,true)
      if(overflow)body.style.setProperty('overflow',overflow,priority);else body.style.removeProperty('overflow')
      if(previous?.isConnected&&typeof previous.focus==='function')previous.focus()
    }
  },[])
  useEffect(()=>{
    const controller=new AbortController(),gesturePointers=pointers.current;let current=true,task=null,destroyed=false,loadTimer
    const destroy=()=>{if(!destroyed&&task){destroyed=true;Promise.resolve(task.destroy()).catch(()=>{})}}
    setResource(null);setStatus('loading');setError('');setPageNumber(1);setZoom(1);setRenderZoom(1);setRenderError('')
    const initialize=async()=>{
      const [library,bytes]=await Promise.all([loadPdfJs(),readFoscPublicationFile({url,expectedUserId:userId,signal:controller.signal})])
      if(!current||activeScope.current!==scope)return null
      task=library.getDocument({...pdfResourceOptions,data:bytes})
      const document=await task.promise
      if(!current||activeScope.current!==scope){destroy();return null}
      if(!Number.isInteger(document.numPages)||document.numPages<1||document.numPages>20)throw Object.assign(new Error('large'),{code:'large'})
      return{scope,pdf:document,destroy}
    }
    Promise.race([initialize(),new Promise((_,reject)=>{loadTimer=window.setTimeout(()=>{controller.abort();reject(new Error('timeout'))},20000)})]).then(value=>{
      window.clearTimeout(loadTimer)
      if(current&&activeScope.current===scope&&value){setResource(value);setStatus('ready')}
    }).catch(failure=>{
      window.clearTimeout(loadTimer);controller.abort();destroy()
      if(current&&activeScope.current===scope){setError(failure?.code==='auth_changed'?'session':failure?.code==='large'?'large':'error');setStatus('error')}
      current=false
    })
    return()=>{current=false;window.clearTimeout(loadTimer);controller.abort();gesturePointers.clear();pinch.current=null;anchor.current=null;destroy()}
  },[url,userId,attempt,scope])
  useEffect(()=>{
    const node=viewport.current
    const measure=()=>setLayoutWidth(current=>{const width=node.clientWidth||Math.min(900,window.innerWidth)-24;return current===width?current:width})
    measure()
    const observer=typeof ResizeObserver==='function'?new ResizeObserver(measure):null
    observer?.observe(node);window.addEventListener('resize',measure)
    return()=>{observer?.disconnect();window.removeEventListener('resize',measure)}
  },[])
  const changeZoom=useCallback((value,x,y,preview=false)=>{
    const node=viewport.current,next=clampPdfZoom(value)
    if(!node||next===zoomRef.current)return
    const box=node.getBoundingClientRect(),px=Number.isFinite(x)?x-box.left:node.clientWidth/2,py=Number.isFinite(y)?y-box.top:node.clientHeight/2
    const previous=zoomRef.current
    anchor.current={left:(node.scrollLeft+px)/previous*next-px,top:(node.scrollTop+py)/previous*next-py}
    const paper=canvas.current,width=Number.parseFloat(paper?.style.width),height=Number.parseFloat(paper?.style.height)
    if(paper&&Number.isFinite(width)&&Number.isFinite(height)){
      paper.style.width=width/previous*next+'px';paper.style.height=height/previous*next+'px'
      node.scrollLeft=Math.max(0,anchor.current.left);node.scrollTop=Math.max(0,anchor.current.top);anchor.current=null
    }
    zoomRef.current=next;setZoom(next)
    // During a pinch, scale existing pixels; commit a sharp render on release.
    if(!preview)setRenderZoom(next)
  },[])
  useEffect(()=>{
    const node=viewport.current
    const wheel=event=>{if(event.ctrlKey){event.preventDefault();changeZoom(zoomRef.current*Math.exp(-event.deltaY*0.01),event.clientX,event.clientY)}}
    node.addEventListener('wheel',wheel,{passive:false})
    return()=>node.removeEventListener('wheel',wheel)
  },[changeZoom])
  useEffect(()=>{
    if(!pdf||!layoutWidth)return undefined
    let alive=true,valid=true,task=null,page=null,buffer=null,renderTimer
    const disposeBuffer=()=>{if(buffer){buffer.width=0;buffer.height=0}}
    const cancelRender=()=>{try{task?.cancel()}catch{}}
    const cleanupPage=()=>{try{page?.cleanup?.()}catch{}}
    setRenderBusy(true);setRenderError('')
    const draw=async()=>{
      page=await pdf.getPage(pageNumber)
      if(!alive||!valid)return
      const node=canvas.current,scroll=viewport.current,base=page.getViewport({scale:1})
      const size=pdfCanvasSize(base,layoutWidth,renderZoom,window.devicePixelRatio)
      const display=pdfCanvasSize(base,layoutWidth,zoomRef.current,window.devicePixelRatio)
      node.style.width=display.cssWidth+'px';node.style.height=display.cssHeight+'px'
      if(anchor.current){scroll.scrollLeft=Math.max(0,anchor.current.left);scroll.scrollTop=Math.max(0,anchor.current.top);anchor.current=null}
      // Render offscreen and retain the last completed bitmap during zoom.
      buffer=document.createElement('canvas');buffer.width=size.width;buffer.height=size.height
      const context=buffer.getContext('2d',{alpha:false})
      if(!context)throw new Error('canvas')
      task=page.render({canvasContext:context,viewport:page.getViewport({scale:size.scale}),transform:[size.ratio,0,0,size.ratio,0,0],background:'rgb(255,255,255)'})
      await task.promise
      if(!alive||!valid)return
      const target=node.getContext('2d',{alpha:false})
      if(!target)throw new Error('canvas')
      node.width=size.width;node.height=size.height;target.drawImage(buffer,0,0)
    }
    const deadline=new Promise((_,reject)=>{
      renderTimer=window.setTimeout(()=>{
        valid=false
        // The same deadline covers getPage() and the actual render promise.
        // Invalidate before cancelling so a late worker reply cannot commit.
        cancelRender();try{destroyDocument?.()}catch{};disposeBuffer()
        reject(Object.assign(new Error('render_timeout'),{code:'render_timeout'}))
      },15000)
    })
    Promise.race([draw(),deadline]).then(()=>{
      if(alive&&valid)setRenderBusy(false)
    }).catch(failure=>{
      if(!alive||failure?.name==='RenderingCancelledException')return
      setRenderBusy(false)
      if(failure?.code==='render_timeout'){
        setResource(null);setStatus('error');setError('error');setRenderError('')
      }else setRenderError(failure?.code==='large'?'large':'error')
    }).finally(()=>{
      window.clearTimeout(renderTimer);disposeBuffer()
      if(!valid||!alive)cleanupPage()
    })
    return()=>{alive=false;valid=false;window.clearTimeout(renderTimer);cancelRender();if(!task)cleanupPage()}
  },[pdf,pageNumber,renderZoom,layoutWidth,destroyDocument])
  const down=event=>{
    if(event.target?.closest?.('button,a,input,select,textarea,[role="button"]'))return
    if(event.button!==0&&event.pointerType!=='touch')return
    viewport.current.setPointerCapture?.(event.pointerId)
    pointers.current.set(event.pointerId,{x:event.clientX,y:event.clientY})
    if(pointers.current.size===2){
      const [a,b]=[...pointers.current.values()]
      pinch.current={distance:Math.hypot(a.x-b.x,a.y-b.y)||1,zoom:zoomRef.current}
    }
  }
  const move=event=>{
    const before=pointers.current.get(event.pointerId)
    if(!before)return
    const next={x:event.clientX,y:event.clientY};pointers.current.set(event.pointerId,next)
    if(pointers.current.size===1){viewport.current.scrollLeft-=next.x-before.x;viewport.current.scrollTop-=next.y-before.y}
    else if(pointers.current.size===2&&pinch.current){
      const [a,b]=[...pointers.current.values()]
      changeZoom(pinch.current.zoom*Math.hypot(a.x-b.x,a.y-b.y)/pinch.current.distance,(a.x+b.x)/2,(a.y+b.y)/2,true)
    }
  }
  const up=event=>{pointers.current.delete(event.pointerId);if(pointers.current.size<2&&pinch.current){pinch.current=null;setRenderZoom(zoomRef.current)};try{viewport.current.releasePointerCapture?.(event.pointerId)}catch{}}
  const changePage=value=>{pointers.current.clear();pinch.current=null;anchor.current=null;setPageNumber(Math.max(1,Math.min(pdf.numPages,value)));viewport.current.scrollTop=0;viewport.current.scrollLeft=0}
  return createPortal(<div className="hb-fosc-pdf-overlay"><section ref={dialog} className="hb-fosc-pdf-dialog" role="dialog" aria-modal="true" aria-label={text.title+(title?' · '+title:'')}>
    <header className="hb-fosc-pdf-header"><div><h2>{text.title}</h2>{title&&<p>{title}</p>}</div><button data-pdf-close type="button" onClick={()=>close.current?.()} aria-label={text.close}><X size={22}/></button></header>
    <div className="hb-fosc-pdf-tools">
      <button type="button" aria-label={text.out} title={text.out} disabled={!pdf||zoom<=0.5} onClick={()=>changeZoom(zoom-0.25)}><ZoomOut size={20}/></button>
      <span className="hb-fosc-pdf-zoom">{Math.round(zoom*100)}%</span>
      <button type="button" aria-label={text.in} title={text.in} disabled={!pdf||zoom>=4} onClick={()=>changeZoom(zoom+0.25)}><ZoomIn size={20}/></button>
      <button type="button" aria-label={text.reset} title={text.reset} disabled={!pdf} onClick={()=>changeZoom(1)}><RotateCcw size={18}/></button>
      {pdf&&pdf.numPages>1&&<div className="hb-fosc-pdf-pages"><button type="button" aria-label={text.prev} disabled={pageNumber<=1} onClick={()=>changePage(pageNumber-1)}><ChevronLeft size={18}/></button><span>{text.page} {pageNumber} {text.of} {pdf.numPages}</span><button type="button" aria-label={text.next} disabled={pageNumber>=pdf.numPages} onClick={()=>changePage(pageNumber+1)}><ChevronRight size={18}/></button></div>}
    </div>
    <div ref={viewport} className="hb-fosc-pdf-viewport" tabIndex={0} role="region" aria-label={text.document} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up}>
      {status==='loading'&&<p className="hb-fosc-pdf-status" role="status">{text.loading}</p>}
      {(status==='error'||renderError)&&<div className="hb-fosc-pdf-status" role="alert"><p>{text[renderError||error]||text.error}</p><button type="button" onClick={()=>setAttempt(value=>value+1)}>{text.retry}</button></div>}
      {pdf&&<div className="hb-fosc-pdf-paper" aria-busy={renderBusy}><canvas ref={canvas} role="img" aria-label={text.page+' '+pageNumber+(title?' · '+title:'')}/></div>}
    </div>
    <p className="hb-fosc-pdf-hint">{text.hint}</p>
  </section></div>,document.body)
}
